import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode, type AnchorHTMLAttributes } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { Gamepad2, Heart, Play } from "lucide-react";
import type { ApiPlayable } from "@hive/sdk";
import { coverGradient, playableName, playablePreviewVideo } from "../../lib/playable";
import { playablePath } from "../../lib/playableKind";
import { playableCoverImageProps } from "../../lib/playableCoverImage";
import { formatCount } from "../../lib/format";
import { useContentImpression } from "../../lib/useContentImpression";
import { track, type ContentProperties } from "../../lib/analytics";
import { COVER_RATIO, HERO_RATIO, distributionPreviewBounds } from "../../lib/distributionPreview";
import { beginCarouselDrag } from "../../lib/carouselDrag";
import { Avatar } from "../ui/Avatar";
import { CoverImage } from "../ui/CoverImage";
import { PreviewVideo } from "./PreviewVideo";

interface CardData {
	item?: ApiPlayable;
	title?: string | null;
	imageUrl?: string | null;
	href?: string;
	properties?: ContentProperties;
	hero?: boolean;
}
interface Preview {
	id: number;
	data: CardData;
	anchor: DOMRect;
	source: HTMLElement;
	posterSrc?: string;
	scrollTarget: HTMLElement | null;
	horizontalTarget: HTMLElement | null;
	playVideo: boolean;
	keyboard: boolean;
	closing?: boolean;
}
interface PreviewControls {
	open: (data: CardData, anchor: HTMLElement, keyboard: boolean) => void;
	leave: (source: HTMLElement) => void;
	close: () => void;
}
const PreviewContext = createContext<PreviewControls | null>(null);
const PreviewActivityContext = createContext(false);
export function useDistributionPreviewActive() { return useContext(PreviewActivityContext); }

const PREVIEW_DURATION = 320;
const PREVIEW_EASING = "cubic-bezier(.25,.1,.25,1)";

/** One active preview; outgoing cards finish shrinking in the same portal before removal. */
export function DistributionPreviewProvider({ children, hoverOnlyVideo = false }: { children: ReactNode; hoverOnlyVideo?: boolean }) {
	const [previews, setPreviews] = useState<Preview[]>([]);
	const previewsRef = useRef(previews);
	previewsRef.current = previews;
	const pending = useRef<{ source: HTMLElement; timer: ReturnType<typeof setTimeout> } | null>(null);
	const leaveTimers = useRef(new Map<HTMLElement, ReturnType<typeof setTimeout>>());
	const nextId = useRef(0);
	const location = useLocation();
	const cancelPending = useCallback(() => {
		if (pending.current) clearTimeout(pending.current.timer);
		pending.current = null;
	}, []);
	const cancelLeave = useCallback((source?: HTMLElement) => {
		for (const [element, timer] of leaveTimers.current) {
			if (source && source !== element) continue;
			clearTimeout(timer);
			leaveTimers.current.delete(element);
		}
	}, []);
	const close = useCallback(() => { cancelPending(); cancelLeave(); setPreviews([]); }, [cancelPending, cancelLeave]);
	const controls = useMemo<PreviewControls>(() => ({
		open(data, element, keyboard) {
			cancelPending(); cancelLeave(element);
			if (!keyboard && !window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;
			pending.current = { source: element, timer: setTimeout(() => {
				pending.current = null;
				const cover = element.querySelector(".distribution-cover");
				if (!element.isConnected || !cover) return;
				const image = cover.querySelector("img");
				const preview: Preview = { id: ++nextId.current, data, source: element, anchor: cover.getBoundingClientRect(), keyboard,
					posterSrc: image?.complete && image.naturalWidth ? image.currentSrc : undefined,
					playVideo: !hoverOnlyVideo || !keyboard, scrollTarget: element.closest<HTMLElement>("[data-catalogue-scroll]"),
					horizontalTarget: element.closest<HTMLElement>("[data-explore-scroll]") };
				setPreviews((current) => {
					const existing = current.some((entry) => entry.source === element);
					const entries = current.map((entry) => entry.source === element
						? { ...entry, closing: false, keyboard, playVideo: preview.playVideo }
						: { ...entry, closing: true });
					return existing ? entries : [...entries, preview];
				});
			}, keyboard || previewsRef.current.some((entry) => entry.source === element) ? 0 : 80) };
		},
		leave(source) {
			if (pending.current?.source === source) cancelPending();
			cancelLeave(source);
			leaveTimers.current.set(source, setTimeout(() => {
				leaveTimers.current.delete(source);
				setPreviews((current) => current.map((entry) => entry.source === source ? { ...entry, closing: true } : entry));
			}, 70));
		},
		close,
	}), [cancelPending, cancelLeave, close, hoverOnlyVideo]);

	useEffect(() => { close(); }, [location.key, close]);
	useEffect(() => {
		const escape = (event: KeyboardEvent) => { if (event.key === "Escape") close(); };
		// Also cancel a pending open when a rail or the page moves.
		window.addEventListener("scroll", close, true);
		window.addEventListener("resize", close);
		window.addEventListener("blur", close);
		window.addEventListener("keydown", escape);
		return () => {
			cancelPending(); cancelLeave();
			window.removeEventListener("scroll", close, true);
			window.removeEventListener("resize", close);
			window.removeEventListener("blur", close);
			window.removeEventListener("keydown", escape);
		};
	}, [cancelPending, cancelLeave, close]);

	return <PreviewContext.Provider value={controls}>
		<PreviewActivityContext.Provider value={previews.length > 0}>{children}</PreviewActivityContext.Provider>
		{previews.length > 0 && createPortal(previews.map((preview) =>
			<CardPreview key={preview.id} preview={preview}
				onEnter={() => { cancelPending(); cancelLeave(preview.source); setPreviews((current) => current.map((entry) => ({ ...entry, closing: entry.id !== preview.id }))); }}
				onLeave={() => controls.leave(preview.source)} onOpen={close}
				onClosed={() => setPreviews((current) => current.filter((entry) => entry.id !== preview.id || !entry.closing))} />),
			document.body,
		)}
	</PreviewContext.Provider>;
}

function CardLink({ data, children, ...props }: {
	data: CardData;
	children: ReactNode;
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href">) {
	const href = data.href ?? (data.item ? playablePath(data.item) : null);
	if (!href) return <div className={props.className}>{children}</div>;
	return href.startsWith("/")
		? <Link to={href} {...props}>{children}</Link>
		: <a href={href} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>;
}

function PreviewCardLink({ data, children }: { data: CardData; children: ReactNode }) {
	const preview = useContext(PreviewContext);
	return <CardLink data={data} className="distribution-card-link" aria-label={data.title ?? (data.item ? playableName(data.item) : undefined)}
			onPointerEnter={(event) => {
				if (event.pointerType === "mouse") preview?.open(data, event.currentTarget, false);
			}}
			onPointerLeave={(event) => preview?.leave(event.currentTarget)}
			onFocus={(event) => {
				if (event.currentTarget.matches(":focus-visible")) preview?.open(data, event.currentTarget, true);
			}}
			onBlur={(event) => {
				if (!(event.relatedTarget instanceof Element) || !event.relatedTarget.closest(".distribution-preview")) preview?.close();
			}}
			onClick={() => { preview?.close(); if (data.properties) track("content_opened", data.properties); }}>
		{children}
	</CardLink>;
}

export function DistributionCard(data: CardData & { item: ApiPlayable; properties: ContentProperties; coverSizes?: string; coverOnly?: boolean; featured?: boolean }) {
	const { t } = useTranslation();
	const ref = useContentImpression(data.properties);
	const title = data.title ?? playableName(data.item);
	const author = data.item.author?.displayName ?? data.item.author?.handle ?? t("playable.anonymousAuthor");
	return <article ref={ref} className="distribution-card" data-hero={data.hero || undefined} data-featured={data.featured || undefined}
		data-preview-video={playablePreviewVideo(data.item) ?? undefined}>
		<PreviewCardLink data={data}>
			<div className="distribution-card-media">
				<CardCover data={data} sizes={data.coverSizes ?? "180px"} />
				{data.hero ? <HeroDetails data={data} /> : null}
			</div>
			{data.hero ? null : !data.coverOnly ? <div className="distribution-caption">
				<Avatar name={author} src={data.item.author?.avatarUrl} size={20} />
				<h3>{title}</h3>
			</div> : <span className="sr-only">{title}</span>}
		</PreviewCardLink>
	</article>;
}

/** Pure CMS creatives share the landscape preview, without inventing game metadata. */
export function DistributionHeroCreative(data: Pick<CardData, "title" | "imageUrl" | "href">) {
	const banner = { ...data, hero: true };
	return <article className="distribution-card" data-hero>
		<PreviewCardLink data={banner}>
			<div className="distribution-card-media">
				<CardCover data={banner} sizes="560px" />
				<HeroDetails data={banner} />
			</div>
		</PreviewCardLink>
	</article>;
}

function HeroDetails({ data }: { data: CardData }) {
	const title = data.title ?? (data.item ? playableName(data.item) : null);
	return <div className="distribution-hero-details">
		{title ? <h3>{title}</h3> : null}
		{data.item ? <CardMetadata item={data.item} /> : null}
	</div>;
}

function CardMetadata({ item }: { item: ApiPlayable }) {
	const { t, i18n } = useTranslation();
	const firstTag = item.tags?.[0];
	const tag = firstTag ? (firstTag.startsWith("#") ? firstTag : "#" + firstTag) : null;
	return <div className="distribution-preview-meta">
		{tag ? <span className="distribution-preview-tag" title={tag}>{tag}</span> : null}
		<div className="distribution-preview-stats">
			{[
				{ Icon: Play, value: item.playCount ?? 0, label: t("play.playsLabel") },
				{ Icon: Heart, value: item.likeCount ?? 0, label: t("play.likesLabel") },
			].map(({ Icon, value, label }) => <span key={label} className="distribution-preview-count"
				role="img" aria-label={`${label}: ${value.toLocaleString(i18n.language)}`} title={`${label}: ${value.toLocaleString(i18n.language)}`}>
				<Icon size={12} aria-hidden="true" />{formatCount(value)}
			</span>)}
		</div>
	</div>;
}

function CardCover({ data, sizes, eager = false, posterSrc }: { data: CardData; sizes: string; eager?: boolean; posterSrc?: string }) {
	const image = data.imageUrl ?? data.item?.coverUrl;
	const props = image ? data.imageUrl ? { src: image } : playableCoverImageProps(image, sizes, data.hero ? 640 : 320) : null;
	const fallback = <span className="grid size-full place-items-center" style={data.item ? { background: coverGradient(data.item) } : undefined}>
		<Gamepad2 size={28} className="text-white/70" />
	</span>;
	return <div className="distribution-cover" style={data.hero ? { aspectRatio: HERO_RATIO } : undefined}>
		{posterSrc ? <img src={posterSrc} alt="" className="size-full object-cover" />
			: props ? <CoverImage {...props} alt="" loading={eager ? "eager" : "lazy"} className="size-full" fallback={fallback} /> : fallback}
	</div>;
}

function CardPreview({ preview, onEnter, onLeave, onOpen, onClosed }: {
	preview: Preview;
	onEnter: () => void;
	onLeave: () => void;
	onOpen: () => void;
	onClosed: () => void;
}) {
	const { data, anchor, keyboard, closing = false } = preview;
	const element = useRef<HTMLDivElement>(null);
	const animations = useRef<Animation[]>([]);
	const onClosedRef = useRef(onClosed);
	onClosedRef.current = onClosed;
	const videoUrl = data.item ? playablePreviewVideo(data.item) : null;
	const [videoFailed, setVideoFailed] = useState(false);
	useEffect(() => {
		const node = element.current;
		const target = preview.scrollTarget;
		if (!node || !target) return;
		// A portal is outside the scroll pane, so native wheel chaining cannot reach it.
		// Forward the first gesture, then close the preview so momentum reaches the cards.
		const wheel = (event: WheelEvent) => {
			if (event.ctrlKey) return;
			event.preventDefault();
			event.stopPropagation();
			const horizontal = event.shiftKey || Math.abs(event.deltaX) > Math.abs(event.deltaY);
			const destination = horizontal ? preview.horizontalTarget ?? target : target;
			const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? (horizontal ? destination.clientWidth : destination.clientHeight) : 1;
			destination.scrollBy({ top: horizontal ? 0 : event.deltaY * unit,
				left: horizontal ? (event.deltaX || event.deltaY) * unit : 0, behavior: "instant" });
			onOpen();
		};
		node.addEventListener("wheel", wheel, { passive: false });
		return () => node.removeEventListener("wheel", wheel);
	}, [preview.scrollTarget, preview.horizontalTarget, onOpen]);
	useEffect(() => { setVideoFailed(false); }, [videoUrl]);
	const title = data.title ?? (data.item ? playableName(data.item) : undefined);
	const bounds = distributionPreviewBounds(anchor, { width: window.innerWidth, height: window.innerHeight }, data.hero ? HERO_RATIO : COVER_RATIO);
	useLayoutEffect(() => {
		const node = element.current;
		if (!node) return;
		const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
		const origin = `translate(${anchor.left - bounds.left}px, ${anchor.top - bounds.top}px) scale(${anchor.width / bounds.width})`;
		if (reduced || typeof node.animate !== "function") return;
		const options = { duration: PREVIEW_DURATION, easing: PREVIEW_EASING, fill: "both" as const };
		animations.current = [node.animate([
			{ transform: origin, outlineColor: "transparent", boxShadow: "0 0 0 0 #9b5cff00, 0 0 0 #0000, 0 0 0 #9b5cff00" },
			{ transform: "translate(0, 0) scale(1)", outlineColor: "#b496ffb3", boxShadow: "0 0 0 4px #9b5cff15, 0 8px 28px #0006, 0 0 22px #9b5cff44" },
		], options)];
		const details = node.querySelector(".distribution-preview-details");
		if (details) animations.current.push(details.animate([
			{ opacity: 0, transform: "translateY(4px)", offset: 0 },
			{ opacity: 0, transform: "translateY(4px)", offset: .2 },
			{ opacity: 1, transform: "translateY(0)", offset: 1 },
		], options));
		return () => { animations.current.forEach((animation) => animation.cancel()); animations.current = []; };
	}, [anchor, bounds.left, bounds.top, bounds.width]);
	useLayoutEffect(() => {
		const primary = animations.current[0];
		if (!primary) { if (closing) onClosedRef.current(); return; }
		// Reverse the existing timeline rather than restarting from another scale.
		// A fast leave/re-enter continues from the exact frame currently on screen.
		if (closing && Number(primary.currentTime) <= 0) { onClosedRef.current(); return; }
		for (const animation of animations.current) {
			animation.updatePlaybackRate(closing ? -1 : 1);
			animation.play();
		}
		primary.onfinish = () => { if (closing) onClosedRef.current(); };
		return () => { primary.onfinish = null; };
	}, [closing, anchor, bounds.left, bounds.top, bounds.width]);
	return <div ref={element} className="distribution-preview" style={bounds} data-hero={data.hero || undefined} data-closing={closing}
		data-keyboard={keyboard} onPointerEnter={onEnter} onPointerLeave={onLeave} onBlur={onOpen}
		onDragStart={(event) => event.preventDefault()}
		onPointerDown={(event) => { if (preview.horizontalTarget) beginCarouselDrag(event.nativeEvent, preview.horizontalTarget, onOpen); }}>
		<CardLink data={data} tabIndex={-1} aria-label={title}
			onClick={() => { if (data.properties) track("content_opened", data.properties); onOpen(); }}>
			<div className="distribution-preview-art">
				<CardCover data={data} sizes={bounds.width + "px"} posterSrc={preview.posterSrc} eager />
				{preview.playVideo && videoUrl && !videoFailed ? <PreviewVideo
					url={videoUrl} play={!closing} priority={closing ? 0 : 2}
					className="distribution-preview-video"
					onFailed={() => setVideoFailed(true)}
				/> : null}
				{data.hero ? <HeroDetails data={data} /> : data.item ? <div className="distribution-preview-details">
					<h3>{title}</h3>
					<CardMetadata item={data.item} />
				</div> : null}
			</div>
		</CardLink>
	</div>;
}
